import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Fetch } from '../../utils/Utils.js';
import { formatDate as formatDateLocale } from '../../../utils/dateFormat.js';
import LotNavRow from './LotNavRow.js';
import LotCustomFields from './LotCustomFields.js';
import LotProjects from './LotProjects.js';
import '../../weight/lots/WeightLots.css';

export const formatLotCode = (lotId, prefix) =>
  lotId != null ? `${prefix}-${String(lotId).padStart(4, '0')}` : '-';

const formatDate = (dateString) => {
  if (!dateString) return '—';
  const s = dateString.length === 10 ? dateString + 'T12:00:00' : dateString;
  const d = new Date(s);
  return isNaN(d) ? dateString : formatDateLocale(d);
};

const EditableNameField = ({ lot, onSaved }) => {
  const { t }     = useTranslation();
  const [editing, setEditing] = useState(false);
  const [draft,   setDraft]   = useState(lot.name || '');

  const save = () => {
    setEditing(false);
    if (draft.trim() !== (lot.name || '')) {
      Fetch('api/data/lots', 'PATCH',
        JSON.stringify({ _id: lot._id, name: draft.trim() }),
        () => onSaved?.('name', draft.trim()), () => {}
      );
    }
  };

  if (editing) {
    return (
      <input className="wld-name-input" value={draft}
        onChange={e => setDraft(e.target.value)}
        onBlur={save} onKeyDown={e => e.key === 'Enter' && save()}
        placeholder={t('rename_lot_placeholder')} maxLength={40} autoFocus />
    );
  }
  return (
    <span className="wld-name" onClick={() => setEditing(true)}>
      {lot.name || <span className="wld-empty">{t('rename_lot_placeholder')}</span>}
      <span className="wld-edit-hint"> ✎</span>
    </span>
  );
};

// EditableMatchesField lets a rancher add nicknames/aliases for this lot —
// same "name;nickname1;nickname2" convention as Area/Breed/Death Cause
// (see AddArea.js), so WhatsApp/voice input can reference the lot by a name
// it actually goes by instead of only its generated code (see
// internal/chat/preprocess in the API repo). Optional: a lot with no
// nicknames still matches by its plain Name.
const EditableMatchesField = ({ lot, onSaved }) => {
  const { t }     = useTranslation();
  const [editing, setEditing] = useState(false);
  const [draft,   setDraft]   = useState(lot.matches || '');

  const save = () => {
    setEditing(false);
    if (draft.trim() !== (lot.matches || '')) {
      Fetch('api/data/lots', 'PATCH',
        JSON.stringify({ _id: lot._id, matches: draft.trim() }),
        () => onSaved?.('matches', draft.trim()), () => {}
      );
    }
  };

  if (editing) {
    return (
      <input className="wld-matches-input" value={draft}
        onChange={e => setDraft(e.target.value)}
        onBlur={save} onKeyDown={e => e.key === 'Enter' && save()}
        placeholder={t('nicknames_placeholder')} autoFocus />
    );
  }
  return (
    <div className="wld-matches" onClick={() => setEditing(true)}>
      <span className="wld-matches-label">{t('nicknames')}: </span>
      {lot.matches || <span className="wld-empty">{t('nicknames_hint')}</span>}
      <span className="wld-edit-hint"> ✎</span>
    </div>
  );
};

// Exported so type-specific detail pages can compose stat grids.
// onClick makes the tile editable (wl-stat-card-editable), e.g. a list's closing date.
export const StatCard = ({ label, value, onClick }) => (
  <div className={`wl-stat-card${onClick ? ' wl-stat-card-editable' : ''}`} onClick={onClick}>
    <div className="wl-stat-label">{label}{onClick && <span className="wld-edit-hint"> ✎</span>}</div>
    <div className="wl-stat-value">{value ?? '—'}</div>
  </div>
);

// LotDetail — shared detail shell for all lot types.
//
// Props:
//   lot           — the lot document from DB (required)
//   prefix        — display prefix string e.g. "HERD", "REPRO", "WEIGHT"
//   basePath      — route base e.g. "/herd/lots" (used to build sub-page links)
//   onFieldSaved  — callback(field, value) called after a successful PATCH
//   stats         — optional React node rendered in the stat cards grid
//   animalSummary — optional string that overrides the auto-fetched animal count
//   showAnimals   — default true; set false to hide the Animals row entirely
//                   (e.g. a weight lot whose tracking_method doesn't involve
//                   per-animal weigh-ins) — reproduction/herd callers never
//                   pass this and keep today's always-shown behavior
//   headerBadge   — optional React node rendered next to the lot code (e.g.
//                   a weight lot's tracking-method pill)
//   whatsappTip   — optional <DetailWhatsAppTip>, rendered under the header
//   children      — additional <LotNavRow> elements for type-specific sub-pages
const LotDetail = ({ lot, prefix, basePath, onFieldSaved, stats, animalSummary, animalsTo, animalsState, showAnimals = true, headerBadge, whatsappTip, children }) => {
  const { t } = useTranslation();
  const [animalCount, setAnimalCount] = useState(null);
  const [noteCount,   setNoteCount]   = useState(null);

  useEffect(() => {
    if (animalSummary === undefined) {
      Fetch(
        `api/data/lots.animals?search_fields=type,lot_id&search_values=${lot.type},${lot.lot_id}`,
        'get', null,
        // Membership rows are never deleted (see lots.OnlyActive) — a
        // released/archived-cycle animal keeps its row with ended_at set,
        // so it must be excluded or this count never drops after release.
        (data) => setAnimalCount((data || []).filter(a => !a.ended_at).length)
      );
    }
    Fetch(
      `api/data/lots.notes?search_fields=lot_id&search_values=${lot.lot_id}`,
      'get', null,
      (data) => setNoteCount((data || []).length)
    );
  }, [lot.lot_id, lot.type, animalSummary]);

  const displayAnimalSummary = animalSummary !== undefined
    ? animalSummary
    : (animalCount !== null
        ? (animalCount > 0 ? String(animalCount) : t('no_animals'))
        : '…');

  const displayNoteSummary = noteCount !== null
    ? (noteCount > 0 ? String(noteCount) : t('noNotes'))
    : '…';

  return (
    <div className="wld-page">
      <div className="wld-header">
        <div className="wld-header-top">
          <div className="wl-detail-code">{formatLotCode(lot.lot_id, prefix)}</div>
          {headerBadge}
        </div>
        <EditableNameField lot={lot} onSaved={onFieldSaved} />
        <EditableMatchesField lot={lot} onSaved={onFieldSaved} />
        <div className="wld-meta">
          <span>{t('lot_created_at')}: {formatDate(lot.created_at)}</span>
        </div>
      </div>

      {lot.status !== 'archived' && whatsappTip}

      {stats && (
        <div className="wl-stats-grid" style={{ marginTop: 16 }}>
          {stats}
        </div>
      )}

      <div className="wld-nav-list" style={{ marginTop: 16 }}>
        {showAnimals && (
          <LotNavRow
            label={t('tagged_animals')}
            summary={displayAnimalSummary}
            to={animalsTo || `${basePath}/${lot.lot_id}/animals`}
            state={animalsState}
          />
        )}
        <LotNavRow
          label={t('lotNotes')}
          summary={displayNoteSummary}
          to={`${basePath}/${lot.lot_id}/notes`}
        />
        <LotNavRow
          label={t(lot.type === 'reproduction' ? 'protocol_progress' : 'plan_progress')}
          summary={lot.protocol_id ? t(lot.type === 'reproduction' ? 'protocol_name' : 'plan_name') : t('not_assigned')}
          to={`${basePath}/${lot.lot_id}/protocol`}
        />
        {children}
      </div>

      <LotProjects lot={lot} onChange={(ids) => onFieldSaved?.('project_ids', ids)} />

      <LotCustomFields lot={lot} />
    </div>
  );
};

export default LotDetail;
